import { useCallback, useEffect, useRef } from 'react';
import { Text, XStack, YStack } from 'tamagui';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  RowFocusContext,
  useRowFocusFieldContext,
  useRowFocusState,
} from '../hooks/useRowFocus';
import { TimeInput } from './TimeInput';
import { timeToMinutes } from '../utils/time-utils';

function compareTime(a: string, b: string): number {
  return timeToMinutes(a) - timeToMinutes(b);
}

export type TimeRangeInputProps = {
  startTime: string;
  endTime: string;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  startPlaceholder?: string | undefined;
  endPlaceholder?: string | undefined;
  showFormatHint?: boolean | undefined;
  /** When true, drop the surrounding border + surface bg so the component
   *  can be embedded inside a parent that already provides a frame
   *  (e.g. FieldRow). Start/end clamping + format hint still apply. */
  borderless?: boolean | undefined;
  /** Forwarded from either inner input. Fires when focus enters the range
   *  (start OR end). Useful when a parent draws its own focus ring. */
  onFocus?: (() => void) | undefined;
  /** Fires when focus leaves both inputs. */
  onBlur?: (() => void) | undefined;
  /** When false, both inner TimeInputs become read-only. Defaults to true. */
  editable?: boolean | undefined;
  /**
   * Called when the component auto-clamps to keep `start <= end`. The
   * `direction` argument names which half was adjusted ("start" when
   * the end time bumped the start back, "end" when the start time
   * pushed the end forward). When omitted, clamping still happens
   * silently (existing behaviour). Per ADR-0076 Phase 4. */
  onClampWarning?: ((direction: 'start' | 'end') => void) | undefined;
  /**
   * Render a "1h 30m" duration string between the two halves. Defaults
   * to false (existing "–" separator).
   */
  showDuration?: boolean | undefined;
};

/** One-line time range: start – end using two TimeInputs. */
export function TimeRangeInput({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  startPlaceholder = '09:00',
  endPlaceholder = '10:00',
  showFormatHint = true,
  borderless = false,
  onFocus,
  onBlur,
  editable = true,
  onClampWarning,
  showDuration = false,
}: TimeRangeInputProps) {
  const theme = useThemeColors();
  // Chain into the parent row reporter if one is present (FieldRow,
  // FormRow). Without this, installing our own provider
  // below would shadow the parent and a TimeRangeInput inside a
  // FieldRow would no longer light the FieldRow on focus. Per ADR-0075
  // we also propagate the parent's error / disabled / describedby
  // through so the chained TimeInputs paint $borderError when set.
  const outerField = useRowFocusFieldContext();
  const parentReporter = outerField.reporter;
  const hasError = Boolean(outerField.error);
  const isDisabled = Boolean(outerField.disabled);
  const { focused, reporter: ownReporter } = useRowFocusState();
  const chainedReporter = useCallback<typeof ownReporter>(
    (next) => {
      ownReporter(next);
      parentReporter?.(next);
    },
    [ownReporter, parentReporter],
  );

  // Notify the caller on edge transitions only — re-firing onFocus on
  // each half-toggle would spam callers that draw their own focus state.
  const wasFocused = useRef(false);
  useEffect(() => {
    if (focused && !wasFocused.current) onFocus?.();
    if (!focused && wasFocused.current) onBlur?.();
    wasFocused.current = focused;
  }, [focused, onFocus, onBlur]);

  const focusBorderColor = hasError
    ? theme.borderError
    : focused
      ? theme.borderFocus
      : theme.borderDefault;
  const isValidTime = (v: string) => /^\d{2}:\d{2}$/.test(v);

  const handleStartChange = useCallback((value: string) => {
    onStartTimeChange(value);
    if (isValidTime(value) && isValidTime(endTime) && compareTime(value, endTime) > 0) {
      onEndTimeChange(value);
      onClampWarning?.('end');
    }
  }, [endTime, onStartTimeChange, onEndTimeChange, onClampWarning]);

  const handleEndChange = useCallback((value: string) => {
    onEndTimeChange(value);
    if (isValidTime(value) && isValidTime(startTime) && compareTime(value, startTime) < 0) {
      onStartTimeChange(value);
      onClampWarning?.('start');
    }
  }, [startTime, onStartTimeChange, onEndTimeChange, onClampWarning]);

  const durationLabel = (() => {
    if (!showDuration) return null;
    if (!isValidTime(startTime) || !isValidTime(endTime)) return null;
    const start = timeToMinutes(startTime);
    const end = timeToMinutes(endTime);
    const diff = end - start;
    if (!Number.isFinite(diff) || diff <= 0) return null;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  })();

  const containerProps = borderless
    ? { borderWidth: 0 as const }
    : {
        borderWidth: focused ? 2 : 1,
        borderColor: focusBorderColor,
        borderRadius: 10,
        backgroundColor: '$surface' as const,
        overflow: 'hidden' as const,
      };

  return (
    <RowFocusContext.Provider
      value={{
        reporter: chainedReporter,
        ...(outerField.controlId ? { controlId: outerField.controlId } : null),
        ...(outerField.messageId ? { messageId: outerField.messageId } : null),
        ...(hasError ? { error: true } : null),
        ...(isDisabled ? { disabled: true } : null),
      }}
    >
      <YStack gap="$1" width="100%">
        {showFormatHint && startTime && endTime && compareTime(endTime, startTime) < 0 ? <Text fontSize="$1" color="$textError" marginLeft={2}>End time must be after start time</Text> : null}
        <XStack alignItems="center" width="100%" {...containerProps}>
          <XStack flex={1} minWidth={0}>
            <TimeInput
              value={startTime}
              onChangeText={handleStartChange}
              placeholder={startPlaceholder}
              showTimeIcon={false}
              showTimePicker={false}
              showFormatHint={false}
              editable={editable}
            />
          </XStack>
          {durationLabel ? (
            <Text color="$textSubdued" fontSize="$2" flexShrink={0} paddingHorizontal="$2">
              {durationLabel}
            </Text>
          ) : (
            <Text color="$textSubdued" fontSize="$3" flexShrink={0} paddingHorizontal="$2">–</Text>
          )}
          <XStack flex={1} minWidth={0}>
            <TimeInput
              value={endTime}
              onChangeText={handleEndChange}
              placeholder={endPlaceholder}
              showTimeIcon={false}
              showTimePicker={false}
              showFormatHint={false}
              editable={editable}
            />
          </XStack>
        </XStack>
      </YStack>
    </RowFocusContext.Provider>
  );
}

TimeRangeInput.displayName = 'TimeRangeInput';
